package com.alpha.showcase.common.cache.dao

import androidx.room3.EntityInsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.performSuspending
import androidx.sqlite.SQLiteStatement
import com.alpha.showcase.common.cache.entity.CachedItem
import kotlin.Boolean
import kotlin.Int
import kotlin.Long
import kotlin.String
import kotlin.Suppress
import kotlin.collections.List
import kotlin.collections.MutableList
import kotlin.collections.mutableListOf
import kotlin.reflect.KClass

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "MemberExtensionConflict"])
internal class CachedItemDao_Impl(
  __db: RoomDatabase,
) : CachedItemDao {
  private val __db: RoomDatabase

  private val __insertAdapterOfCachedItem: EntityInsertAdapter<CachedItem>
  init {
    this.__db = __db
    this.__insertAdapterOfCachedItem = object : EntityInsertAdapter<CachedItem>() {
      protected override fun createQuery(): String = "INSERT OR IGNORE INTO `cached_items` (`id`,`source_type`,`source_key`,`parent_path`,`name`,`path`,`is_directory`,`size`,`mime_type`,`media_kind`,`modified_time`,`created_at`,`last_accessed`,`is_recursive`,`metadata`,`sync_version`) VALUES (nullif(?, 0),?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: CachedItem) {
        statement.bindLong(1, entity.id)
        statement.bindText(2, entity.sourceType)
        statement.bindText(3, entity.sourceKey)
        statement.bindText(4, entity.parentPath)
        statement.bindText(5, entity.name)
        statement.bindText(6, entity.path)
        val _tmp: Int = if (entity.isDirectory) 1 else 0
        statement.bindLong(7, _tmp.toLong())
        statement.bindLong(8, entity.size)
        statement.bindText(9, entity.mimeType)
        statement.bindLong(10, entity.mediaKind.toLong())
        statement.bindLong(11, entity.modifiedTime)
        statement.bindLong(12, entity.createdAt)
        statement.bindLong(13, entity.lastAccessed)
        val _tmp_1: Int = if (entity.isRecursive) 1 else 0
        statement.bindLong(14, _tmp_1.toLong())
        val _tmpMetadata: String? = entity.metadata
        if (_tmpMetadata == null) {
          statement.bindNull(15)
        } else {
          statement.bindText(15, _tmpMetadata)
        }
        statement.bindLong(16, entity.syncVersion)
      }
    }
  }

  public override suspend fun insertOrIgnore(items: List<CachedItem>): List<Long> = performSuspending(__db, false, true) { _connection ->
    val _result: List<Long> = __insertAdapterOfCachedItem.insertAndReturnIdsList(_connection, items)
    _result
  }

  public override suspend fun getBySource(sourceType: String, sourceKey: String): List<CachedItem> {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        ORDER BY is_directory DESC, name COLLATE NOCASE ASC, path ASC
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: MutableList<CachedItem> = mutableListOf()
        while (_stmt.step()) {
          val _item: CachedItem
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _item = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getBySourceVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
  ): List<CachedItem> {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND sync_version = ?
        |        ORDER BY is_directory DESC, name COLLATE NOCASE ASC, path ASC
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: MutableList<CachedItem> = mutableListOf()
        while (_stmt.step()) {
          val _item: CachedItem
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _item = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun countBySource(sourceType: String, sourceKey: String): Int {
    val _sql: String = """
        |
        |        SELECT COUNT(*) FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        val _result: Int
        if (_stmt.step()) {
          val _tmp: Int
          _tmp = _stmt.getLong(0).toInt()
          _result = _tmp
        } else {
          _result = 0
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun maxSyncVersionBySource(sourceType: String, sourceKey: String): Long? {
    val _sql: String = """
        |
        |        SELECT MAX(sync_version) FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        val _result: Long?
        if (_stmt.step()) {
          val _tmp: Long?
          if (_stmt.isNull(0)) {
            _tmp = null
          } else {
            _tmp = _stmt.getLong(0)
          }
          _result = _tmp
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun hasAnyBySourceVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
  ): Boolean {
    val _sql: String = """
        |
        |        SELECT EXISTS(
        |            SELECT 1 FROM cached_items
        |            WHERE source_type = ? AND source_key = ?
        |            AND sync_version = ?
        |            LIMIT 1
        |        )
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        val _result: Boolean
        if (_stmt.step()) {
          val _tmp: Int
          _tmp = _stmt.getLong(0).toInt()
          _result = _tmp != 0
        } else {
          _result = false
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun countImagesBySourceVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
  ): Int {
    val _sql: String = """
        |
        |        SELECT COUNT(*) FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND sync_version = ?
        |        AND is_directory = 0
        |        AND media_kind = 1
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        val _result: Int
        if (_stmt.step()) {
          val _tmp: Int
          _tmp = _stmt.getLong(0).toInt()
          _result = _tmp
        } else {
          _result = 0
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun countMediaBySourceVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
  ): Int {
    val _sql: String = """
        |
        |        SELECT COUNT(*) FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND sync_version = ?
        |        AND is_directory = 0
        |        AND media_kind IN (1, 2)
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        val _result: Int
        if (_stmt.step()) {
          val _tmp: Int
          _tmp = _stmt.getLong(0).toInt()
          _result = _tmp
        } else {
          _result = 0
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun countImagesBySource(sourceType: String, sourceKey: String): Int {
    val _sql: String = """
        |
        |        SELECT COUNT(*) FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND is_directory = 0
        |        AND media_kind = 1
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        val _result: Int
        if (_stmt.step()) {
          val _tmp: Int
          _tmp = _stmt.getLong(0).toInt()
          _result = _tmp
        } else {
          _result = 0
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun countMediaBySource(sourceType: String, sourceKey: String): Int {
    val _sql: String = """
        |
        |        SELECT COUNT(*) FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND is_directory = 0
        |        AND media_kind IN (1, 2)
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        val _result: Int
        if (_stmt.step()) {
          val _tmp: Int
          _tmp = _stmt.getLong(0).toInt()
          _result = _tmp
        } else {
          _result = 0
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getImagesPagedByNameAsc(
    sourceType: String,
    sourceKey: String,
    limit: Int,
    offset: Int,
  ): List<CachedItem> {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND is_directory = 0
        |        AND media_kind = 1
        |        ORDER BY name COLLATE NOCASE ASC, path ASC
        |        LIMIT ? OFFSET ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 4
        _stmt.bindLong(_argIndex, offset.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: MutableList<CachedItem> = mutableListOf()
        while (_stmt.step()) {
          val _item: CachedItem
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _item = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getImagesPagedByNameDesc(
    sourceType: String,
    sourceKey: String,
    limit: Int,
    offset: Int,
  ): List<CachedItem> {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND is_directory = 0
        |        AND media_kind = 1
        |        ORDER BY name COLLATE NOCASE DESC, path DESC
        |        LIMIT ? OFFSET ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 4
        _stmt.bindLong(_argIndex, offset.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: MutableList<CachedItem> = mutableListOf()
        while (_stmt.step()) {
          val _item: CachedItem
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _item = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getImagesPagedByDateAsc(
    sourceType: String,
    sourceKey: String,
    limit: Int,
    offset: Int,
  ): List<CachedItem> {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND is_directory = 0
        |        AND media_kind = 1
        |        ORDER BY modified_time ASC, path ASC
        |        LIMIT ? OFFSET ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 4
        _stmt.bindLong(_argIndex, offset.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: MutableList<CachedItem> = mutableListOf()
        while (_stmt.step()) {
          val _item: CachedItem
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _item = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getImagesPagedByDateDesc(
    sourceType: String,
    sourceKey: String,
    limit: Int,
    offset: Int,
  ): List<CachedItem> {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND is_directory = 0
        |        AND media_kind = 1
        |        ORDER BY modified_time DESC, path DESC
        |        LIMIT ? OFFSET ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 4
        _stmt.bindLong(_argIndex, offset.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: MutableList<CachedItem> = mutableListOf()
        while (_stmt.step()) {
          val _item: CachedItem
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _item = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getMediaPagedByNameAsc(
    sourceType: String,
    sourceKey: String,
    limit: Int,
    offset: Int,
  ): List<CachedItem> {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND is_directory = 0
        |        AND media_kind IN (1, 2)
        |        ORDER BY name COLLATE NOCASE ASC, path ASC
        |        LIMIT ? OFFSET ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 4
        _stmt.bindLong(_argIndex, offset.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: MutableList<CachedItem> = mutableListOf()
        while (_stmt.step()) {
          val _item: CachedItem
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _item = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getMediaPagedByNameDesc(
    sourceType: String,
    sourceKey: String,
    limit: Int,
    offset: Int,
  ): List<CachedItem> {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND is_directory = 0
        |        AND media_kind IN (1, 2)
        |        ORDER BY name COLLATE NOCASE DESC, path DESC
        |        LIMIT ? OFFSET ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 4
        _stmt.bindLong(_argIndex, offset.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: MutableList<CachedItem> = mutableListOf()
        while (_stmt.step()) {
          val _item: CachedItem
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _item = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getMediaPagedByDateAsc(
    sourceType: String,
    sourceKey: String,
    limit: Int,
    offset: Int,
  ): List<CachedItem> {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND is_directory = 0
        |        AND media_kind IN (1, 2)
        |        ORDER BY modified_time ASC, path ASC
        |        LIMIT ? OFFSET ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 4
        _stmt.bindLong(_argIndex, offset.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: MutableList<CachedItem> = mutableListOf()
        while (_stmt.step()) {
          val _item: CachedItem
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _item = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getMediaPagedByDateDesc(
    sourceType: String,
    sourceKey: String,
    limit: Int,
    offset: Int,
  ): List<CachedItem> {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND is_directory = 0
        |        AND media_kind IN (1, 2)
        |        ORDER BY modified_time DESC, path DESC
        |        LIMIT ? OFFSET ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 4
        _stmt.bindLong(_argIndex, offset.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: MutableList<CachedItem> = mutableListOf()
        while (_stmt.step()) {
          val _item: CachedItem
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _item = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getImagesPagedByNameAscVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
    limit: Int,
    offset: Int,
  ): List<CachedItem> {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND sync_version = ?
        |        AND is_directory = 0 AND media_kind = 1
        |        ORDER BY name COLLATE NOCASE ASC, path ASC LIMIT ? OFFSET ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        _argIndex = 4
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 5
        _stmt.bindLong(_argIndex, offset.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: MutableList<CachedItem> = mutableListOf()
        while (_stmt.step()) {
          val _item: CachedItem
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _item = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getImagesPagedByNameDescVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
    limit: Int,
    offset: Int,
  ): List<CachedItem> {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND sync_version = ?
        |        AND is_directory = 0 AND media_kind = 1
        |        ORDER BY name COLLATE NOCASE DESC, path DESC LIMIT ? OFFSET ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        _argIndex = 4
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 5
        _stmt.bindLong(_argIndex, offset.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: MutableList<CachedItem> = mutableListOf()
        while (_stmt.step()) {
          val _item: CachedItem
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _item = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getImagesPagedByDateAscVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
    limit: Int,
    offset: Int,
  ): List<CachedItem> {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND sync_version = ?
        |        AND is_directory = 0 AND media_kind = 1
        |        ORDER BY modified_time ASC, path ASC LIMIT ? OFFSET ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        _argIndex = 4
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 5
        _stmt.bindLong(_argIndex, offset.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: MutableList<CachedItem> = mutableListOf()
        while (_stmt.step()) {
          val _item: CachedItem
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _item = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getImagesPagedByDateDescVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
    limit: Int,
    offset: Int,
  ): List<CachedItem> {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND sync_version = ?
        |        AND is_directory = 0 AND media_kind = 1
        |        ORDER BY modified_time DESC, path DESC LIMIT ? OFFSET ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        _argIndex = 4
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 5
        _stmt.bindLong(_argIndex, offset.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: MutableList<CachedItem> = mutableListOf()
        while (_stmt.step()) {
          val _item: CachedItem
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _item = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getImagesPagedByIdAscVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
    limit: Int,
    offset: Int,
  ): List<CachedItem> {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND sync_version = ?
        |        AND is_directory = 0 AND media_kind = 1
        |        ORDER BY id ASC LIMIT ? OFFSET ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        _argIndex = 4
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 5
        _stmt.bindLong(_argIndex, offset.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: MutableList<CachedItem> = mutableListOf()
        while (_stmt.step()) {
          val _item: CachedItem
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _item = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getMediaPagedByIdAscVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
    limit: Int,
    offset: Int,
  ): List<CachedItem> {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND sync_version = ?
        |        AND is_directory = 0 AND media_kind IN (1, 2)
        |        ORDER BY id ASC LIMIT ? OFFSET ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        _argIndex = 4
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 5
        _stmt.bindLong(_argIndex, offset.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: MutableList<CachedItem> = mutableListOf()
        while (_stmt.step()) {
          val _item: CachedItem
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _item = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getMediaPagedByNameAscVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
    limit: Int,
    offset: Int,
  ): List<CachedItem> {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND sync_version = ?
        |        AND is_directory = 0 AND media_kind IN (1, 2)
        |        ORDER BY name COLLATE NOCASE ASC, path ASC LIMIT ? OFFSET ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        _argIndex = 4
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 5
        _stmt.bindLong(_argIndex, offset.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: MutableList<CachedItem> = mutableListOf()
        while (_stmt.step()) {
          val _item: CachedItem
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _item = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getMediaPagedByNameDescVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
    limit: Int,
    offset: Int,
  ): List<CachedItem> {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND sync_version = ?
        |        AND is_directory = 0 AND media_kind IN (1, 2)
        |        ORDER BY name COLLATE NOCASE DESC, path DESC LIMIT ? OFFSET ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        _argIndex = 4
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 5
        _stmt.bindLong(_argIndex, offset.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: MutableList<CachedItem> = mutableListOf()
        while (_stmt.step()) {
          val _item: CachedItem
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _item = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getMediaPagedByDateAscVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
    limit: Int,
    offset: Int,
  ): List<CachedItem> {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND sync_version = ?
        |        AND is_directory = 0 AND media_kind IN (1, 2)
        |        ORDER BY modified_time ASC, path ASC LIMIT ? OFFSET ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        _argIndex = 4
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 5
        _stmt.bindLong(_argIndex, offset.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: MutableList<CachedItem> = mutableListOf()
        while (_stmt.step()) {
          val _item: CachedItem
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _item = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getMediaPagedByDateDescVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
    limit: Int,
    offset: Int,
  ): List<CachedItem> {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND sync_version = ?
        |        AND is_directory = 0 AND media_kind IN (1, 2)
        |        ORDER BY modified_time DESC, path DESC LIMIT ? OFFSET ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        _argIndex = 4
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 5
        _stmt.bindLong(_argIndex, offset.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: MutableList<CachedItem> = mutableListOf()
        while (_stmt.step()) {
          val _item: CachedItem
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _item = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getByPathVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
    path: String,
  ): CachedItem? {
    val _sql: String = """
        |
        |        SELECT * FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND sync_version = ? AND path = ?
        |        LIMIT 1
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        _argIndex = 4
        _stmt.bindText(_argIndex, path)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfParentPath: Int = getColumnIndexOrThrow(_stmt, "parent_path")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfPath: Int = getColumnIndexOrThrow(_stmt, "path")
        val _columnIndexOfIsDirectory: Int = getColumnIndexOrThrow(_stmt, "is_directory")
        val _columnIndexOfSize: Int = getColumnIndexOrThrow(_stmt, "size")
        val _columnIndexOfMimeType: Int = getColumnIndexOrThrow(_stmt, "mime_type")
        val _columnIndexOfMediaKind: Int = getColumnIndexOrThrow(_stmt, "media_kind")
        val _columnIndexOfModifiedTime: Int = getColumnIndexOrThrow(_stmt, "modified_time")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
        val _columnIndexOfLastAccessed: Int = getColumnIndexOrThrow(_stmt, "last_accessed")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfMetadata: Int = getColumnIndexOrThrow(_stmt, "metadata")
        val _columnIndexOfSyncVersion: Int = getColumnIndexOrThrow(_stmt, "sync_version")
        val _result: CachedItem?
        if (_stmt.step()) {
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpParentPath: String
          _tmpParentPath = _stmt.getText(_columnIndexOfParentPath)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpPath: String
          _tmpPath = _stmt.getText(_columnIndexOfPath)
          val _tmpIsDirectory: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsDirectory).toInt()
          _tmpIsDirectory = _tmp != 0
          val _tmpSize: Long
          _tmpSize = _stmt.getLong(_columnIndexOfSize)
          val _tmpMimeType: String
          _tmpMimeType = _stmt.getText(_columnIndexOfMimeType)
          val _tmpMediaKind: Int
          _tmpMediaKind = _stmt.getLong(_columnIndexOfMediaKind).toInt()
          val _tmpModifiedTime: Long
          _tmpModifiedTime = _stmt.getLong(_columnIndexOfModifiedTime)
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          val _tmpLastAccessed: Long
          _tmpLastAccessed = _stmt.getLong(_columnIndexOfLastAccessed)
          val _tmpIsRecursive: Boolean
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp_1 != 0
          val _tmpMetadata: String?
          if (_stmt.isNull(_columnIndexOfMetadata)) {
            _tmpMetadata = null
          } else {
            _tmpMetadata = _stmt.getText(_columnIndexOfMetadata)
          }
          val _tmpSyncVersion: Long
          _tmpSyncVersion = _stmt.getLong(_columnIndexOfSyncVersion)
          _result = CachedItem(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpParentPath,_tmpName,_tmpPath,_tmpIsDirectory,_tmpSize,_tmpMimeType,_tmpMediaKind,_tmpModifiedTime,_tmpCreatedAt,_tmpLastAccessed,_tmpIsRecursive,_tmpMetadata,_tmpSyncVersion)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun countMediaBeforeIdVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
    maxMediaKind: Int,
    id: Long,
  ): Int {
    val _sql: String = """
        |
        |        SELECT COUNT(*) FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND sync_version = ?
        |        AND is_directory = 0 AND media_kind BETWEEN 1 AND ?
        |        AND id < ?
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        _argIndex = 4
        _stmt.bindLong(_argIndex, maxMediaKind.toLong())
        _argIndex = 5
        _stmt.bindLong(_argIndex, id)
        val _result: Int
        if (_stmt.step()) {
          val _tmp: Int
          _tmp = _stmt.getLong(0).toInt()
          _result = _tmp
        } else {
          _result = 0
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun countMediaBeforeNameAscVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
    maxMediaKind: Int,
    name: String,
    path: String,
  ): Int {
    val _sql: String = """
        |
        |        SELECT COUNT(*) FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND sync_version = ?
        |        AND is_directory = 0 AND media_kind BETWEEN 1 AND ?
        |        AND (name COLLATE NOCASE < ? OR (name COLLATE NOCASE = ? AND path < ?))
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        _argIndex = 4
        _stmt.bindLong(_argIndex, maxMediaKind.toLong())
        _argIndex = 5
        _stmt.bindText(_argIndex, name)
        _argIndex = 6
        _stmt.bindText(_argIndex, name)
        _argIndex = 7
        _stmt.bindText(_argIndex, path)
        val _result: Int
        if (_stmt.step()) {
          val _tmp: Int
          _tmp = _stmt.getLong(0).toInt()
          _result = _tmp
        } else {
          _result = 0
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun countMediaBeforeNameDescVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
    maxMediaKind: Int,
    name: String,
    path: String,
  ): Int {
    val _sql: String = """
        |
        |        SELECT COUNT(*) FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND sync_version = ?
        |        AND is_directory = 0 AND media_kind BETWEEN 1 AND ?
        |        AND (name COLLATE NOCASE > ? OR (name COLLATE NOCASE = ? AND path > ?))
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        _argIndex = 4
        _stmt.bindLong(_argIndex, maxMediaKind.toLong())
        _argIndex = 5
        _stmt.bindText(_argIndex, name)
        _argIndex = 6
        _stmt.bindText(_argIndex, name)
        _argIndex = 7
        _stmt.bindText(_argIndex, path)
        val _result: Int
        if (_stmt.step()) {
          val _tmp: Int
          _tmp = _stmt.getLong(0).toInt()
          _result = _tmp
        } else {
          _result = 0
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun countMediaBeforeDateAscVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
    maxMediaKind: Int,
    modifiedTime: Long,
    path: String,
  ): Int {
    val _sql: String = """
        |
        |        SELECT COUNT(*) FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND sync_version = ?
        |        AND is_directory = 0 AND media_kind BETWEEN 1 AND ?
        |        AND (modified_time < ? OR (modified_time = ? AND path < ?))
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        _argIndex = 4
        _stmt.bindLong(_argIndex, maxMediaKind.toLong())
        _argIndex = 5
        _stmt.bindLong(_argIndex, modifiedTime)
        _argIndex = 6
        _stmt.bindLong(_argIndex, modifiedTime)
        _argIndex = 7
        _stmt.bindText(_argIndex, path)
        val _result: Int
        if (_stmt.step()) {
          val _tmp: Int
          _tmp = _stmt.getLong(0).toInt()
          _result = _tmp
        } else {
          _result = 0
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun countMediaBeforeDateDescVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
    maxMediaKind: Int,
    modifiedTime: Long,
    path: String,
  ): Int {
    val _sql: String = """
        |
        |        SELECT COUNT(*) FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND sync_version = ?
        |        AND is_directory = 0 AND media_kind BETWEEN 1 AND ?
        |        AND (modified_time > ? OR (modified_time = ? AND path > ?))
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        _argIndex = 4
        _stmt.bindLong(_argIndex, maxMediaKind.toLong())
        _argIndex = 5
        _stmt.bindLong(_argIndex, modifiedTime)
        _argIndex = 6
        _stmt.bindLong(_argIndex, modifiedTime)
        _argIndex = 7
        _stmt.bindText(_argIndex, path)
        val _result: Int
        if (_stmt.step()) {
          val _tmp: Int
          _tmp = _stmt.getLong(0).toInt()
          _result = _tmp
        } else {
          _result = 0
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteBySource(sourceType: String, sourceKey: String) {
    val _sql: String = "DELETE FROM cached_items WHERE source_type = ? AND source_key = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteBySourceAndOldSyncVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
  ) {
    val _sql: String = """
        |
        |        DELETE FROM cached_items
        |        WHERE source_type = ? AND source_key = ? AND sync_version != ?
        |        
        """.trimMargin()
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteBySourceExceptVersions(
    sourceType: String,
    sourceKey: String,
    keepVersion: Long,
    graceVersion: Long,
  ) {
    val _sql: String = """
        |
        |        DELETE FROM cached_items
        |        WHERE source_type = ? AND source_key = ?
        |        AND sync_version NOT IN (?, ?)
        |        
        """.trimMargin()
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, keepVersion)
        _argIndex = 4
        _stmt.bindLong(_argIndex, graceVersion)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteBySourceAndSyncVersion(
    sourceType: String,
    sourceKey: String,
    syncVersion: Long,
  ) {
    val _sql: String = """
        |
        |        DELETE FROM cached_items
        |        WHERE source_type = ? AND source_key = ? AND sync_version = ?
        |        
        """.trimMargin()
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _argIndex = 3
        _stmt.bindLong(_argIndex, syncVersion)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun updateLastAccessed(
    sourceType: String,
    sourceKey: String,
    accessedAt: Long,
  ) {
    val _sql: String = """
        |
        |        UPDATE cached_items
        |        SET last_accessed = ?
        |        WHERE source_type = ? AND source_key = ?
        |        
        """.trimMargin()
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, accessedAt)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 3
        _stmt.bindText(_argIndex, sourceKey)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
